import React, { useState } from 'react';
import {
    Box,
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {LOGINAPI} from "../../common";

const DeleteAccount = () => {
    const [schoolId, setSchoolId] = useState('');
    const [loginId, setLoginId] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [openConfirm, setOpenConfirm] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!schoolId || !loginId) {
            setError('Please fill in all fields');
            return;
        }

        setOpenConfirm(true);
    };

    const handleConfirmDelete = async () => {
        try {
            // First verify if the account exists
            const checkResponse = await LOGINAPI.get(`/api/master/isSchoolExist/${schoolId}`);
            if (!checkResponse.data) {
                setError('School ID not found');
                setOpenConfirm(false);
                return;
            }

            // Proceed with deletion
            const response = await LOGINAPI.delete(`/api/master/deleteAccount`, {
                data: {
                    schoolId: schoolId,
                    loginId: loginId
                }
            });

            if (response.status === 200) {
                setSuccess('Account deleted successfully');
                setTimeout(() => {
                    navigate('/');
                }, 2000);
            }
        } catch (error) {
            console.error('Delete account error:', error);
            setError(error.response?.data?.message || 'Failed to delete account. Please try again.');
        }
        setOpenConfirm(false);
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#f8fafc',
            py: 4
        }}>
            <Container maxWidth="sm">
                <Paper elevation={0} sx={{
                    p: 4,
                    borderRadius: 2,
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }}>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: '#1a202c' }}>
                            Delete Account
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#4a5568' }}>
                            Please enter your School ID and Login ID to delete your account
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 3, borderRadius: 1 }}>
                            {success}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="School ID"
                            value={schoolId}
                            onChange={(e) => setSchoolId(e.target.value)}
                            margin="normal"
                            required
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            fullWidth
                            label="Login ID"
                            value={loginId}
                            onChange={(e) => setLoginId(e.target.value)}
                            margin="normal"
                            required
                            sx={{ mb: 3 }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            sx={{
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: 1,
                                bgcolor: '#ef4444',
                                '&:hover': {
                                    bgcolor: '#dc2626',
                                },
                            }}
                        >
                            Delete Account
                        </Button>

                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Button
                                onClick={() => navigate('/')}
                                sx={{
                                    color: 'text.secondary',
                                    textTransform: 'none',
                                    fontWeight: 500
                                }}
                            >
                                Cancel and go back
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Container>

            {/* Confirmation Dialog */}
            <Dialog
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Are you sure you want to delete this account?"}
                </DialogTitle>
                <DialogContent>
                    <Typography color="error" sx={{ mt: 2 }}>
                        This action cannot be undone. All data associated with this account will be permanently deleted.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button
                        onClick={() => setOpenConfirm(false)}
                        sx={{
                            color: 'text.secondary',
                            textTransform: 'none',
                            fontWeight: 500
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        sx={{
                            bgcolor: '#ef4444',
                            '&:hover': {
                                bgcolor: '#dc2626',
                            },
                            textTransform: 'none',
                            fontWeight: 500
                        }}
                        autoFocus
                    >
                        Yes, Delete Account
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DeleteAccount;
